import React from 'react';
import { BarChart3 } from 'lucide-react';
import CirclePack from './CirclePack';

const useMediaQuery = (query) => {
  const getMatches = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  };
  const [matches, setMatches] = React.useState(getMatches);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia(query);
    const handleChange = () => setMatches(media.matches);
    handleChange();
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, [query]);

  return matches;
};

const buildTreeRows = (root) => {
  const rows = [];
  const valueOf = (node) => Number(node?.rawValue || 0);

  const collectRows = (node, depth, bucket) => {
    if (!node) return false;
    const children = Array.isArray(node.children) ? node.children : [];
    const childBuckets = children
      .map((child) => {
        const nested = [];
        const visible = collectRows(child, depth + 1, nested);
        return { child, nested, visible };
      })
      .filter((entry) => entry.visible);

    childBuckets.sort((a, b) => {
      const diff = valueOf(b.child) - valueOf(a.child);
      if (diff !== 0) return diff;
      const nameA = (a.child.name || '').toLowerCase();
      const nameB = (b.child.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const isVisible = valueOf(node) > 0 || childBuckets.length > 0;
    if (!isVisible) return false;

    bucket.push({ node, depth });
    childBuckets.forEach((entry) => {
      bucket.push(...entry.nested);
    });
    return true;
  };

  collectRows(root, 0, rows);
  return rows;
};

const TreeView = ({ root, unitLabel, barClassName, formatValue }) => {
  const rows = React.useMemo(() => buildTreeRows(root), [root]);
  const maxValue = React.useMemo(
    () => rows.reduce((max, row) => Math.max(max, Number(row.node?.rawValue || 0)), 0),
    [rows]
  );

  if (!rows.length || maxValue <= 0) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400 text-sm">
        Aucune donnée sur la période.
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      {rows.map((row) => {
        const value = Number(row.node?.rawValue || 0);
        const ratio = maxValue > 0 ? (value / maxValue) * 100 : 0;
        const width = value > 0 ? Math.max(ratio, 2) : 0;
        const isProject = row.node?.nodeType === 'project';
        const label = row.node?.name || 'Sans titre';
        const projectNumber = row.node?.project_number;
        return (
          <div
            key={`${row.node?.id}-${row.depth}`}
            className="flex items-center gap-3 border-b border-slate-100 py-2 last:border-b-0"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0" style={{ paddingLeft: row.depth * 14 }}>
                  <span className={`h-2 w-2 rounded-full ${isProject ? 'bg-emerald-400' : 'bg-indigo-400'}`} />
                  <span className={`truncate ${isProject ? 'text-sm text-slate-700' : 'text-sm font-semibold text-slate-900'}`}>
                    {label}
                  </span>
                  {isProject && projectNumber !== undefined && projectNumber !== null && (
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">
                      #{projectNumber}
                    </span>
                  )}
                </div>
                <span className="text-xs font-semibold text-slate-600 whitespace-nowrap">
                  {formatValue(value)} {unitLabel}
                </span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-1.5 rounded-full ${barClassName}`} style={{ width: `${width}%` }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CircleInsightsPanel = ({
  selectedCircle,
  metricsRange,
  metricsRangeValid,
  onMetricsRangeChange,
  currentSlotRange,
  isUsingSlotRange,
  summary,
  loading,
  error,
  hoursHierarchy,
  expensesHierarchy,
}) => {
  const formatNumber = (value, decimals = 0) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '0';
    return num.toLocaleString('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const totalHours = summary?.hours || 0;
  const totalExpenses = summary?.expenses || 0;
  const projectsCount = summary?.projects || 0;
  const coveredCount = summary?.covered || 0;
  const isCompact = useMediaQuery('(max-width: 1023px)');
  const [compactView, setCompactView] = React.useState('hours');
  const [viewMode, setViewMode] = React.useState('structure');

  const updateRange = (key, value) => {
    if (!onMetricsRangeChange) return;
    onMetricsRangeChange((prev) => ({ ...prev, [key]: value }));
  };

  const applyCurrentSlotRange = () => {
    if (!onMetricsRangeChange || !currentSlotRange) return;
    onMetricsRangeChange({
      start: currentSlotRange.start,
      end: currentSlotRange.end,
    });
  };

  const slotButtonDisabled = !currentSlotRange;
  const slotButtonClassName = isUsingSlotRange
    ? 'text-[11px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1 hover:bg-emerald-100'
    : slotButtonDisabled
      ? 'text-[11px] uppercase tracking-wider text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1 cursor-not-allowed'
      : 'text-[11px] uppercase tracking-wider text-slate-600 bg-white border border-slate-200 rounded-full px-2.5 py-1 hover:border-emerald-200 hover:text-emerald-700';

  const valueAccessor = React.useCallback(
    (node) => (typeof node.value === 'number' ? node.value : 0),
    []
  );

  if (!selectedCircle?.id) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md h-full overflow-hidden flex items-center justify-center text-slate-400">
        Sélectionnez un cercle pour afficher les insights.
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md flex flex-col min-h-full">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-indigo-600 font-semibold">
            <BarChart3 size={16} />
            Chiffres du cercle
          </div>
          <h2 className="text-xl font-bold text-slate-900">{selectedCircle?.name || 'Cercle'}</h2>
          <p className="text-slate-500 text-sm">Deux cartes basees sur la consommation.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setViewMode('structure')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              viewMode === 'structure'
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
            }`}
          >
            Structure
          </button>
          <button
            type="button"
            onClick={() => setViewMode('tree')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              viewMode === 'tree'
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
            }`}
          >
            Arbre
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4 shadow-sm mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-100 bg-white/80 p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Temps total</p>
              <p className="text-2xl font-semibold text-slate-800">{formatNumber(totalHours, 1)} h</p>
              <p className="text-[11px] text-slate-500">{coveredCount}/{projectsCount} projets couverts</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-white/80 p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Achats total</p>
              <p className="text-2xl font-semibold text-slate-800">{formatNumber(totalExpenses, 0)} EUR</p>
              <p className="text-[11px] text-slate-500">Somme des depenses du cercle</p>
            </div>
          </div>

          <div className="min-w-[260px]">
            <div className="flex items-center justify-end gap-2 mb-2">
              <button
                type="button"
                onClick={applyCurrentSlotRange}
                className={slotButtonClassName}
                disabled={slotButtonDisabled}
              >
                Slot en cours
              </button>
            </div>
            <div className="grid gap-2">
              <label className="text-[11px] text-slate-500">
                Début
                <input
                  type="date"
                  value={metricsRange?.start || ''}
                  onChange={(event) => updateRange('start', event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1 text-sm focus:border-emerald-300 focus:outline-none"
                />
              </label>
              <label className="text-[11px] text-slate-500">
                Fin
                <input
                  type="date"
                  value={metricsRange?.end || ''}
                  onChange={(event) => updateRange('end', event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1 text-sm focus:border-emerald-300 focus:outline-none"
                />
              </label>
            </div>
          </div>
        </div>

        {!metricsRangeValid && metricsRange?.start && metricsRange?.end && (
          <p className="text-xs text-rose-500 mt-3">La date de fin doit etre apres la date de debut.</p>
        )}
        {error && <p className="text-xs text-rose-500 mt-2">{error}</p>}
        {loading && (
          <p className="text-xs text-slate-400 mt-2">Chargement des chiffres…</p>
        )}
        <p className="text-[11px] text-slate-400 mt-1">
          {viewMode === 'structure'
            ? 'La taille des cercles représente la consommation sur la période.'
            : 'La longueur des barres représente la consommation sur la période.'}
        </p>
      </div>

      {isCompact && (
        <div className="flex items-center justify-center gap-2 mb-3">
          <button
            type="button"
            onClick={() => setCompactView('hours')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              compactView === 'hours'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-200'
            }`}
          >
            Heures
          </button>
          <button
            type="button"
            onClick={() => setCompactView('expenses')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              compactView === 'expenses'
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-slate-500 border-slate-200 hover:border-emerald-200'
            }`}
          >
            Dépenses
          </button>
        </div>
      )}

      <div className={`grid gap-4 flex-1 min-h-0 ${isCompact ? '' : 'lg:grid-cols-2'}`}>
        {(!isCompact || compactView === 'hours') && (
          <div className="rounded-2xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 shadow-sm overflow-hidden flex flex-col min-h-0">
          <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Structure temps</p>
                <p className="text-lg font-semibold text-slate-800">{formatNumber(totalHours, 1)} h</p>
              </div>
              <span className="text-[10px] uppercase text-slate-400">
                {coveredCount}/{projectsCount} projets
              </span>
            </div>
          </div>
          <div className="flex-1 min-h-[360px]">
            {hoursHierarchy ? (
              viewMode === 'structure' ? (
                <div className="h-full w-full p-2">
                  <div className="h-full w-full rounded-xl overflow-hidden">
                    <CirclePack data={hoursHierarchy} valueAccessor={valueAccessor} tight />
                  </div>
                </div>
              ) : (
                <TreeView
                  root={hoursHierarchy}
                  unitLabel="h"
                  barClassName="bg-indigo-500"
                  formatValue={(value) => formatNumber(value, 1)}
                />
              )
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Aucune donnée sur la période.
              </div>
            )}
          </div>
          </div>
        )}

        {(!isCompact || compactView === 'expenses') && (
          <div className="rounded-2xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 shadow-sm overflow-hidden flex flex-col min-h-0">
          <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Structure achats</p>
                <p className="text-lg font-semibold text-slate-800">{formatNumber(totalExpenses, 0)} EUR</p>
              </div>
              <span className="text-[10px] uppercase text-slate-400">
                {coveredCount}/{projectsCount} projets
              </span>
            </div>
          </div>
          <div className="flex-1 min-h-[360px]">
            {expensesHierarchy ? (
              viewMode === 'structure' ? (
                <div className="h-full w-full p-2">
                  <div className="h-full w-full rounded-xl overflow-hidden">
                    <CirclePack data={expensesHierarchy} valueAccessor={valueAccessor} tight />
                  </div>
                </div>
              ) : (
                <TreeView
                  root={expensesHierarchy}
                  unitLabel="EUR"
                  barClassName="bg-emerald-500"
                  formatValue={(value) => formatNumber(value, 0)}
                />
              )
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Aucune donnée sur la période.
              </div>
            )}
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CircleInsightsPanel;
